'use client';

import { useState } from 'react';
import { Send, Mail, ArrowRight, AlertCircle, Check, Loader2, Bell, Sparkles, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import { notificationService } from '@/services/notificationService';
import { signUpForWaitlist } from '@/services/supabase/waitlist';
import type { ContactFormData } from '@/types/notifications';

const DualCTA = () => {
  // Contact form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    company: '',
    propertyCount: '',
    interestType: undefined,
    message: '',
  });

  // Waitlist state
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistStatus, setWaitlistStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [waitlistError, setWaitlistError] = useState('');

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInterestChange = (value: 'saas' | 'managed') => {
    setFormData(prev => ({ ...prev, interestType: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await notificationService.submitContactForm({
        ...formData,
        source: 'landing_contact_form',
      });
      if (result.success) {
        setIsSubmitted(true);
      } else {
        setError(result.error || 'Failed to submit form. Please try again.');
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!waitlistEmail || !waitlistEmail.includes('@')) {
      setWaitlistStatus('error');
      setWaitlistError('Please enter a valid email address');
      return;
    }
    setWaitlistStatus('loading');
    try {
      await signUpForWaitlist({
        email: waitlistEmail,
        source: 'landing_page',
        interests: ['launch_notification', 'feature_updates', 'founding_member'],
      });
      setWaitlistStatus('success');
      setWaitlistEmail('');
    } catch (err) {
      setWaitlistStatus('error');
      setWaitlistError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  const inputClasses = cn(
    "w-full px-4 py-3 rounded-xl",
    "text-gray-900 placeholder:text-gray-400 dark:text-secondary-100 dark:placeholder:text-secondary-600",
    "bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08]",
    "focus:outline-none focus:border-primary-500/30 focus:bg-gray-50 dark:focus:bg-white/[0.05]",
    "transition-all duration-300"
  );

  return (
    <section id="contact" className="relative py-14 sm:py-20 lg:py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-500/[0.03] to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="font-playfair text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-secondary-100 mb-4">
            Ready to Get Started?
          </h2>
          <p className="max-w-xl mx-auto text-gray-500 dark:text-secondary-400">
            Get in touch or join our waitlist for early access and founding member pricing.
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Form */}
          <div className={cn(
            "p-6 md:p-8 rounded-2xl",
            "bg-white shadow-sm dark:bg-white/[0.03] dark:shadow-none",
            "border border-gray-200 dark:border-white/[0.06]"
          )}>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-secondary-100 mb-6">Contact Us</h3>

            {isSubmitted ? (
              <div className="p-8 rounded-xl bg-gradient-to-br from-success-500/10 to-transparent border border-success-500/20 text-center">
                <div className="w-14 h-14 rounded-full bg-success-500/20 border border-success-500/30 flex items-center justify-center mx-auto mb-4">
                  <Send className="w-7 h-7 text-success-400" />
                </div>
                <h4 className="text-xl font-semibold text-gray-900 dark:text-secondary-100 mb-2">Message Sent!</h4>
                <p className="text-gray-500 dark:text-secondary-400 text-sm">We'll be in touch within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="p-3 rounded-xl bg-danger-500/10 border border-danger-500/20 flex items-center gap-3">
                    <AlertCircle className="w-4 h-4 text-danger-400 flex-shrink-0" />
                    <p className="text-sm text-danger-300">{error}</p>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-secondary-300 mb-1.5">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="John Smith"
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-secondary-300 mb-1.5">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="john@company.com"
                      className={inputClasses}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-secondary-300 mb-1.5">Company</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Acme Properties"
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-secondary-300 mb-1.5">Properties</label>
                    <select
                      name="propertyCount"
                      value={formData.propertyCount}
                      onChange={handleInputChange}
                      className={inputClasses}
                    >
                      <option value="" className="bg-white dark:bg-[#1a1a1a]">Select...</option>
                      <option value="1-5" className="bg-white dark:bg-[#1a1a1a]">1-5</option>
                      <option value="6-25" className="bg-white dark:bg-[#1a1a1a]">6-25</option>
                      <option value="26-100" className="bg-white dark:bg-[#1a1a1a]">26-100</option>
                      <option value="100+" className="bg-white dark:bg-[#1a1a1a]">100+</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-secondary-300 mb-1.5">I'm Interested In</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { value: 'saas' as const, label: 'Axis Datum Platform' },
                      { value: 'managed' as const, label: 'Managed Service (CoreOps)' },
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={cn(
                          "flex items-center justify-center px-4 py-2.5 rounded-xl cursor-pointer",
                          "bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08]",
                          "hover:bg-gray-50 dark:hover:bg-white/[0.05] hover:border-gray-300 dark:hover:border-white/[0.12]",
                          formData.interestType === option.value && "bg-primary-500/10 border-primary-500/30",
                          "transition-all duration-300"
                        )}
                        onClick={() => handleInterestChange(option.value)}
                      >
                        <input
                          type="radio"
                          name="interest"
                          value={option.value}
                          checked={formData.interestType === option.value}
                          onChange={() => handleInterestChange(option.value)}
                          className="sr-only"
                        />
                        <span className="text-sm text-gray-600 dark:text-secondary-300">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-secondary-300 mb-1.5">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Tell us about your needs..."
                    className={cn(inputClasses, "resize-none")}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={cn(
                    "w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl font-semibold",
                    "bg-primary-500 text-white",
                    "hover:bg-primary-400 transition-all duration-300",
                    "shadow-lg dark:shadow-[0_0_30px_-8px_rgba(59,130,246,0.4)]",
                    "animate-glow-pulse",
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  )}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Waitlist Card */}
          <div className="flex flex-col gap-6">
            <div className={cn(
              "p-6 md:p-8 rounded-2xl flex-grow",
              "bg-white dark:bg-white/[0.02] backdrop-blur-sm",
              "border border-gray-200 dark:border-white/[0.08]",
              "shadow-lg dark:shadow-[0_0_40px_-15px_rgba(59,130,246,0.1)]"
            )}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 mb-6">
                <Sparkles className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
                <span className="text-primary-600 dark:text-primary-400 text-xs font-medium">Early Access</span>
              </div>

              <h3 className="font-playfair text-2xl font-bold text-gray-900 dark:text-secondary-100 mb-3">
                Join the Waitlist
              </h3>
              <p className="text-gray-500 dark:text-secondary-400 mb-6 leading-relaxed">
                Get early access when we launch, plus exclusive founding member pricing and feature updates.
              </p>

              <div className="flex flex-wrap gap-4 mb-6">
                {[
                  { icon: Bell, text: 'Launch notification' },
                  { icon: Sparkles, text: 'Feature updates' },
                  { icon: Check, text: 'Founding member pricing' },
                ].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-gray-500 dark:text-secondary-400">
                    <benefit.icon className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
                    <span className="text-xs">{benefit.text}</span>
                  </div>
                ))}
              </div>

              {waitlistStatus === 'success' ? (
                <div className="text-center py-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-500/10 border border-green-500/20 mb-3">
                    <Check className="w-6 h-6 text-green-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-secondary-100 mb-1">You're on the list!</h4>
                  <p className="text-gray-500 dark:text-secondary-400 text-sm">We'll notify you when Axis Datum launches.</p>
                </div>
              ) : (
                <form onSubmit={handleWaitlistSubmit}>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-secondary-500" />
                      <input
                        type="email"
                        value={waitlistEmail}
                        onChange={(e) => {
                          setWaitlistEmail(e.target.value);
                          if (waitlistStatus === 'error') setWaitlistStatus('idle');
                        }}
                        placeholder="Enter your email"
                        className={cn(
                          "w-full pl-10 pr-4 py-3.5 rounded-xl",
                          "bg-white dark:bg-white/[0.03] border",
                          waitlistStatus === 'error'
                            ? "border-red-500/50"
                            : "border-gray-200 dark:border-white/[0.08] focus:border-primary-500/50",
                          "text-gray-900 placeholder-gray-400 dark:text-secondary-100 dark:placeholder-secondary-500",
                          "focus:outline-none focus:ring-2 focus:ring-primary-500/20",
                          "transition-all duration-200"
                        )}
                        disabled={waitlistStatus === 'loading'}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={waitlistStatus === 'loading'}
                      className={cn(
                        "inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl",
                        "bg-primary-500 text-white font-semibold text-sm",
                        "shadow-lg dark:shadow-[0_0_20px_-8px_rgba(59,130,246,0.4)]",
                        "hover:bg-primary-400",
                        "disabled:opacity-60 disabled:cursor-not-allowed",
                        "transition-all duration-300"
                      )}
                    >
                      {waitlistStatus === 'loading' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>Join<ArrowRight className="w-4 h-4" /></>
                      )}
                    </button>
                  </div>
                  {waitlistStatus === 'error' && (
                    <p className="mt-2 text-red-400 text-xs">{waitlistError}</p>
                  )}
                  <p className="mt-3 text-gray-400 dark:text-secondary-500 text-xs">No spam, ever. Unsubscribe anytime.</p>
                </form>
              )}
            </div>

            {/* Quick Info Card */}
            <div className={cn(
              "p-6 rounded-2xl",
              "bg-white shadow-sm dark:bg-white/[0.03] dark:shadow-none",
              "border border-gray-200 dark:border-white/[0.06]"
            )}>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-secondary-100 mb-3">Get in Touch</h4>
              <div className="space-y-3">
                <a
                  href="mailto:hello@axisdatum.com"
                  className="flex items-center gap-3 text-sm text-gray-500 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary-500/10 border border-primary-500/20 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  hello@axisdatum.com
                </a>
                <p className="text-xs text-gray-400 dark:text-secondary-500">We respond to all inquiries within 24 hours.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Veteran-Owned Badge */}
        <div className="mt-16 flex justify-center">
          <div className={cn(
            "inline-flex items-center gap-3 px-6 py-3 rounded-full",
            "bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08]",
            "shadow-sm dark:shadow-none"
          )}>
            <div className="w-8 h-8 rounded-full bg-primary-500/10 border border-primary-500/20 flex items-center justify-center">
              <Shield className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-gray-900 dark:text-secondary-100">Veteran-Owned</span>
              <span className="text-xs text-gray-400 dark:text-secondary-500">Service-Disabled Veteran-Owned Small Business (SDVOSB)</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { DualCTA };
